import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { FaTimes, FaClock, FaMapMarkerAlt, FaUsers, FaPlus } from 'react-icons/fa';
import { EVENT_TYPE_COLORS } from '../../constants/notificationTypes';

const DayEventsModal = ({ 
  isOpen, 
  selectedDate, 
  events = [], 
  onClose, 
  onEventSelect,
  onCreateNew 
}) => {
  const { theme } = useTheme();

  if (!isOpen || !selectedDate) return null;

  const formatTime = (dateTime) => {
    return new Date(dateTime).toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateTitle = (date) => {
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const modalBg = theme === 'dark' ? 'bg-gray-800' : 'bg-white';
  const textColor = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const mutedText = theme === 'dark' ? 'text-gray-400' : 'text-gray-500';
  const borderColor = theme === 'dark' ? 'border-gray-600' : 'border-gray-300';

  // Sort events by time
  const sortedEvents = [...events].sort((a, b) => {
    return new Date(a.CAL_DataOraInizio).getTime() - new Date(b.CAL_DataOraInizio).getTime();
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${modalBg} rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden`}>
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className={`text-lg font-bold ${textColor}`}>
              {formatDateTitle(selectedDate)}
            </h2>
            <p className={`text-sm ${mutedText}`}>
              {events.length === 0 ? 'Nessun evento' : `${events.length} ${events.length === 1 ? 'evento' : 'eventi'}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className={`p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors ${mutedText}`}
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(80vh-120px)]">
          {events.length === 0 ? (
            // Empty state
            <div className="p-6 text-center">
              <div className={`text-4xl ${mutedText} mb-3`}>📅</div>
              <p className={`${mutedText} mb-4`}>
                Nessun evento programmato per questo giorno
              </p>
              <button
                onClick={() => onCreateNew(selectedDate)}
                className="flex items-center gap-2 mx-auto px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <FaPlus className="w-4 h-4" />
                Crea Nuovo Evento
              </button>
            </div>
          ) : (
            // Events list
            <div className="p-4 space-y-3">
              {sortedEvents.map((evento, index) => {
                const colorClass = EVENT_TYPE_COLORS[evento.CAL_TipoEvento] || 'bg-gray-500';
                const isAllDay = evento.CAL_GiornoIntero;
                
                return (
                  <div
                    key={`${evento.CAL_Id_CalendarioEventi}-${index}`}
                    className={`
                      p-3 rounded-lg border cursor-pointer transition-all
                      hover:shadow-md hover:scale-[1.02]
                      ${theme === 'dark' ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}
                    `}
                    onClick={() => onEventSelect(evento)}
                  >
                    {/* Event header */}
                    <div className="flex items-start gap-3">
                      <div className={`w-3 h-3 rounded-full ${colorClass} mt-1 flex-shrink-0`} />
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-medium ${textColor} truncate`}>
                          {evento.CAL_Titolo}
                        </h3>
                        
                        {/* Time */}
                        <div className={`flex items-center gap-1 text-sm ${mutedText} mt-1`}>
                          <FaClock className="w-3 h-3" />
                          {isAllDay ? (
                            <span>Tutto il giorno</span>
                          ) : (
                            <span>
                              {formatTime(evento.CAL_DataOraInizio)}
                              {evento.CAL_DataOraFine && ` - ${formatTime(evento.CAL_DataOraFine)}`}
                            </span>
                          )}
                        </div>

                        {/* Location */}
                        {evento.CAL_Luogo && (
                          <div className={`flex items-center gap-1 text-sm ${mutedText} mt-1`}>
                            <FaMapMarkerAlt className="w-3 h-3" />
                            <span className="truncate">{evento.CAL_Luogo}</span>
                          </div>
                        )}

                        {/* Participants */}
                        {evento.partecipanti && evento.partecipanti.length > 0 && (
                          <div className={`flex items-center gap-1 text-sm ${mutedText} mt-1`}>
                            <FaUsers className="w-3 h-3" />
                            <span className="truncate">
                              {evento.partecipanti.map(p => p.nome).join(', ')}
                            </span>
                          </div>
                        )}

                        {/* Description preview */}
                        {evento.CAL_Descrizione && (
                          <p className={`text-sm ${mutedText} mt-2 line-clamp-2`}>
                            {evento.CAL_Descrizione}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Event type badge */}
                    <div className="flex justify-end mt-2">
                      <span className={`
                        ${colorClass} text-white text-xs px-2 py-1 rounded-full
                      `}>
                        {evento.CAL_TipoEvento}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <button
              onClick={() => onCreateNew(selectedDate)}
              className="flex items-center gap-2 px-3 py-2 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
            >
              <FaPlus className="w-4 h-4" />
              Nuovo Evento
            </button>
            
            <button
              onClick={onClose}
              className={`px-4 py-2 ${theme === 'dark' ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} ${textColor} rounded-lg transition-colors`}
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DayEventsModal;